import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ATTACHMENT_LIMIT_BYTES,
  formatAttachmentSize,
  formatTicketDate,
} from '../utils/supportTicketPortal';
import { optionLabel } from '../utils/supportTicketOptions';

/**
 * Shared read-only formatting surface of the support ticket portal.
 * Depends only on vue-i18n, so page and extracted components can call it
 * without duplicating locale plumbing.
 */
export function useSupportTicketFormatters() {
  const { t, locale } = useI18n();

  const intlLocale = () => String(locale.value || 'pt-BR').replace('_', '-');

  const attachmentLimitLabel = computed(() =>
    formatAttachmentSize(ATTACHMENT_LIMIT_BYTES)
  );

  const localizedDate = value => formatTicketDate(value, intlLocale());

  const priorityLabel = (options, priority) =>
    optionLabel(options, priority) || priority;

  const assistanceLabel = (options, value) =>
    optionLabel(options, value) ||
    value ||
    t('SUPPORT_TICKETS.ASSISTANCE.EMPTY_VALUE');

  const emptyValue = value =>
    value === undefined || value === null || value === ''
      ? t('SUPPORT_TICKETS.ASSISTANCE.EMPTY_VALUE')
      : value;

  const formatAssistanceMoney = (value, currency = 'BRL') => {
    if (!value) return t('SUPPORT_TICKETS.ASSISTANCE.EMPTY_VALUE');
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value;

    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(numericValue);
  };

  const formatAssistanceDecimal = value =>
    value === null || value === undefined || value === ''
      ? t('SUPPORT_TICKETS.ASSISTANCE.EMPTY_VALUE')
      : new Intl.NumberFormat(intlLocale(), {
          maximumFractionDigits: 2,
        }).format(Number(value));

  const humanizeAssistanceValue = value =>
    value
      ? String(value)
          .replaceAll('_', ' ')
          .replace(/^\w/, character => character.toUpperCase())
      : t('SUPPORT_TICKETS.ASSISTANCE.EMPTY_VALUE');

  const errorMessage = error =>
    error?.response?.data?.errors?.join(', ') ||
    error?.response?.data?.message ||
    t('SUPPORT_TICKETS.ALERTS.GENERIC_ERROR');

  return {
    attachmentLimitLabel,
    localizedDate,
    priorityLabel,
    assistanceLabel,
    emptyValue,
    formatAssistanceMoney,
    formatAssistanceDecimal,
    humanizeAssistanceValue,
    errorMessage,
  };
}
